/**
 * Analyze tab — System Checks tools (BIM "Systems Analysis").
 *
 * Real checks over the live project (not stubs):
 * - Duct / Pipe systems: union-find network grouping by endpoint proximity
 *   to find disconnected systems, dangling ends, and zero-length elements;
 *   plus velocity/pressure-drop stats via the parameter propagation the
 *   flow solvers write.
 * - Electrical circuits: elements missing circuit assignment, open wires.
 * Results are reported through the toast host and a text report dialog.
 */

import { BaseTool } from '../BaseTool';
import { bimKernel } from '../../core';
import { useAnalysisStore } from '../../state/useAnalysisStore';

const CONNECT_TOLERANCE = 0.1; // meters

interface NetworkReport {
    networks: number;
    orphans: string[];
    disconnected: string[];
    zeroLength: string[];
}

function endpointsOf(el: any): [number, number, number][] | null {
    const g = el.geometryData;
    if (!g) return null;
    if (Array.isArray(g.path) && g.path.length >= 2) {
        return [g.path[0], g.path[g.path.length - 1]];
    }
    if (Array.isArray(g.start) && Array.isArray(g.end)) {
        return [g.start, g.end];
    }
    return null;
}

/**
 * Group linear MEP elements into connected networks with union-find on
 * endpoint proximity. Returns a report + the per-network totals.
 */
function analyzeNetwork(categories: string[]): NetworkReport & { sizes: number[] } {
    const elements = Array.from(bimKernel.getProject().elements.values()).filter(el =>
        categories.includes(el.category)
    );

    const parent = new Map<string, string>();
    const find = (x: string): string => {
        let r = x;
        while (parent.get(r) !== r) r = parent.get(r)!;
        let cur = x;
        while (parent.get(cur) !== r) {
            const next = parent.get(cur)!;
            parent.set(cur, r);
            cur = next;
        }
        return r;
    };
    const union = (a: string, b: string) => {
        parent.set(find(a), find(b));
    };

    const orphans: string[] = [];
    const zeroLength: string[] = [];
    const points: { id: string; pts: [number, number, number][] }[] = [];

    elements.forEach(el => {
        parent.set(el.id, el.id);
        const pts = endpointsOf(el);
        if (!pts) {
            orphans.push(el.name || el.id);
            return;
        }
        const len = Math.hypot(pts[1][0] - pts[0][0], pts[1][1] - pts[0][1], pts[1][2] - pts[0][2]);
        if (len < 1e-4) zeroLength.push(el.name || el.id);
        points.push({ id: el.id, pts: pts });
    });

    for (let i = 0; i < points.length; i++) {
        for (let j = i + 1; j < points.length; j++) {
            const a = points[i].pts;
            const b = points[j].pts;
            const near = a.some(pa => b.some(pb =>
                Math.hypot(pa[0] - pb[0], pa[1] - pb[1], pa[2] - pb[2]) < CONNECT_TOLERANCE
            ));
            if (near) union(points[i].id, points[j].id);
        }
    }

    const sizes = new Map<string, number>();
    points.forEach(p => {
        const root = find(p.id);
        sizes.set(root, (sizes.get(root) ?? 0) + 1);
    });

    return {
        networks: sizes.size,
        sizes: Array.from(sizes.values()),
        orphans,
        disconnected: points.map(p => p.id).filter(id => (sizes.get(find(id)) ?? 0) <= 1),
        zeroLength,
    };
}

function reportToStore(title: string, lines: string[]): void {
    useAnalysisStore.getState().setReport({ title, lines, generatedOn: Date.now() });
}

function notifyReport(message: string, status: 'info' | 'success' | 'warning' | 'error'): void {
    window.dispatchEvent(new CustomEvent('yap:notify', { detail: { message, status } }));
}

function summarizeFlow(categories: string[], label: string): void {
    const elements = Array.from(bimKernel.getProject().elements.values()).filter(el =>
        categories.includes(el.category)
    );
    const withFlow = elements.filter(el => (el.parameters?.['Flow'] ?? 0) > 0);
    const totalFlow = withFlow.reduce((a, el) => a + Number(el.parameters?.['Flow'] ?? 0), 0);
    const maxVel = withFlow.reduce((a, el) => Math.max(a, Number(el.parameters?.['Velocity'] ?? 0)), 0);
    const totalDrop = withFlow.reduce((a, el) => a + Number(el.parameters?.['PressureDrop'] ?? 0), 0);
    const lines = [
        `${elements.length} ${label.toLowerCase()} element(s) in the model.`,
        `${withFlow.length} element(s) carry flow (run Duct/Pipe flow analysis to populate).`,
        `Total flow: ${totalFlow.toFixed(0)} · Max velocity: ${maxVel.toFixed(2)} m/s · Pressure drop: ${totalDrop.toFixed(1)} Pa`,
    ];
    reportToStore(`${label} Systems Analysis`, lines);
}

export class CheckDuctSystemsTool extends BaseTool {
    id = 'ANALYZE_CHECK_DUCTS';
    name = 'Check Duct Systems';
    cursor = 'default';

    onActivate(): void {
        const report = analyzeNetwork(['Ducts', 'Flex Ducts', 'Duct Placeholders', 'Air Terminals']);
        const lines = [
            `Disconnected systems: ${report.networks}`,
            report.orphans.length ? `Elements with no geometry: ${report.orphans.join(', ')}` : 'No geometry-less ducts.',
            report.disconnected.length ? `Single-element (unconnected) systems: ${report.disconnected.length}` : 'Every duct joins a network.',
            report.zeroLength.length ? `Zero-length segments: ${report.zeroLength.length}` : '',
            ``,
        ].filter(Boolean);
        reportToStore('Duct System Check', lines);
        summarizeFlow(['Ducts', 'Flex Ducts'], 'Duct');
        notifyReport(
            `Duct check: ${report.networks} network(s), ${report.disconnected.length} unconnected element(s). See Results.`,
            report.disconnected.length > 0 ? 'warning' : 'success'
        );
        this.context?.appStore.openManageDialog('analyzeResults');
        this.context?.appStore.setActiveTool('select');
    }

    onPointerDown(): void { }
    onPointerMove(): void { }
    onPointerUp(): void { }
}

export class CheckPipeSystemsTool extends BaseTool {
    id = 'ANALYZE_CHECK_PIPES';
    name = 'Check Pipe Systems';
    cursor = 'default';

    onActivate(): void {
        const report = analyzeNetwork(['Pipes', 'Flex Pipes', 'Pipe Placeholders']);
        const lines = [
            `Disconnected systems: ${report.networks}`,
            report.orphans.length ? `Elements with no geometry: ${report.orphans.join(', ')}` : 'No geometry-less pipes.',
            report.disconnected.length ? `Single-element (unconnected) systems: ${report.disconnected.length}` : 'Every pipe joins a network.',
            ``,
        ].filter(Boolean);
        reportToStore('Pipe System Check', lines);
        summarizeFlow(['Pipes', 'Flex Pipes'], 'Pipe');
        notifyReport(
            `Pipe check: ${report.networks} network(s), ${report.disconnected.length} unconnected element(s). See Results.`,
            report.disconnected.length > 0 ? 'warning' : 'success'
        );
        this.context?.appStore.openManageDialog('analyzeResults');
        this.context?.appStore.setActiveTool('select');
    }

    onPointerDown(): void { }
    onPointerMove(): void { }
    onPointerUp(): void { }
}

export class CheckCircuitsTool extends BaseTool {
    id = 'ANALYZE_CHECK_CIRCUITS';
    name = 'Check Electrical Circuits';
    cursor = 'default';

    onActivate(): void {
        const elements = Array.from(bimKernel.getProject().elements.values());
        const fixtures = elements.filter(el =>
            ['Lighting Fixtures', 'Electrical Equipment', 'Devices', 'Electrical Fixtures', 'Electrical Devices'].includes(el.category)
        );
        const unassigned = fixtures.filter(el => !el.parameters?.['Circuit'] && !el.parameters?.['Panel']);
        const wires = elements.filter(el => el.category === 'Wires');
        const openWires = wires.filter(w => {
            const g = (w as any).geometryData;
            return !g?.startDeviceId || !g?.endDeviceId;
        });
        const lines = [
            `Electrical elements: ${fixtures.length}`,
            unassigned.length ? `No circuit assigned (${unassigned.length}): ${unassigned.slice(0, 8).map(e => e.name || e.id).join(', ')}${unassigned.length > 8 ? ' …' : ''}` : 'All fixtures belong to a circuit.',
            `Wires: ${wires.length}`,
            openWires.length ? `Open wires (missing device on one end): ${openWires.length}` : 'All wires connected between devices.',
        ];
        reportToStore('Electrical Circuit Check', lines);
        const issues = unassigned.length + openWires.length;
        notifyReport(
            issues === 0
                ? 'Electrical circuits OK — no unassigned fixtures or open wires.'
                : `Circuit check: ${issues} issue(s) found. See Results.`,
            issues === 0 ? 'success' : 'warning'
        );
        this.context?.appStore.openManageDialog('analyzeResults');
        this.context?.appStore.setActiveTool('select');
    }

    onPointerDown(): void { }
    onPointerMove(): void { }
    onPointerUp(): void { }
}
